/**
 * CreatorEarningsSection
 * Compact section in Settings showing creator status and earnings summary.
 * Links to the full CreatorEarningsModal dashboard.
 */

import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useCreatorEarnings } from '@hooks/useCreatorEarnings';
import { CREATOR_ELIGIBILITY } from '@parlay-picker/shared/creatorPayout';
import { formatCents } from '@parlay-picker/shared/formatting';
import { cn } from '@utils/cn';
import {
  AlertTriangle,
  BadgeCheck,
  DollarSign,
  ExternalLink,
  Link as LinkIcon,
} from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';

interface CreatorEarningsSectionProps {
  onOpenEarningsDashboard: () => void;
}

export function CreatorEarningsSection({
  onOpenEarningsDashboard,
}: CreatorEarningsSectionProps): ReactElement {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const { status, statusLoading, fetchStatus } = useCreatorEarnings();
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleConnectStripe = async () => {
    setConnectLoading(true);
    try {
      const res = await authFetch('/api/creator/connect/onboard', { method: 'POST' });
      const data = await res.json();
      if (data.data?.url) {
        window.location.href = data.data.url;
      } else {
        showToast('Failed to start Stripe onboarding. Please try again.', 'error');
      }
    } catch {
      showToast('Failed to connect to Stripe. Please try again.', 'error');
    } finally {
      setConnectLoading(false);
    }
  };

  if (statusLoading) {
    return (
      <div className="bg-theme-surface rounded-xl p-6 border border-theme-border mb-6">
        <h2 className="text-lg font-semibold text-theme-text mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Creator Earnings
        </h2>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-theme-border rounded w-3/4" />
          <div className="h-4 bg-theme-border rounded w-1/2" />
        </div>
      </div>
    );
  }

  const isCreator = status?.isCreator ?? false;
  const resolvedPicks = status?.resolvedPicks ?? 0;
  const progressPercent = Math.min(
    (resolvedPicks / CREATOR_ELIGIBILITY.MIN_RESOLVED_PICKS) * 100,
    100,
  );

  return (
    <div className="bg-theme-surface rounded-xl p-6 border border-theme-border mb-6">
      <h2 className="text-lg font-semibold text-theme-text mb-4 flex items-center gap-2">
        <DollarSign className="w-5 h-5" />
        Creator Earnings
      </h2>

      {!isCreator ? (
        // Not yet a creator — show progress toward 50 picks
        <div>
          <p className="text-sm text-theme-text-secondary mb-3">
            Resolve {CREATOR_ELIGIBILITY.MIN_RESOLVED_PICKS - resolvedPicks} more picks to unlock
            creator earnings. Earn ${(CREATOR_ELIGIBILITY.FOLLOW_PRICE_CENTS / 100).toFixed(2)} per
            eligible follower each month.
          </p>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-theme-border rounded-full overflow-hidden">
              <div
                className="h-full bg-theme-primary rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs text-theme-text-tertiary whitespace-nowrap">
              {resolvedPicks}/{CREATOR_ELIGIBILITY.MIN_RESOLVED_PICKS} picks
            </span>
          </div>
        </div>
      ) : (
        // Creator — show summary stats + dashboard button
        <div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-xs text-theme-text-tertiary uppercase tracking-wider mb-1">
                Total Earned
              </div>
              <div
                className={cn(
                  'text-lg font-bold',
                  (status?.totalEarnedCents ?? 0) > 0
                    ? 'text-theme-success'
                    : 'text-theme-text-secondary',
                )}
              >
                {formatCents(status?.totalEarnedCents ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-theme-text-tertiary uppercase tracking-wider mb-1">
                Followers
              </div>
              <div className="text-lg font-bold text-theme-text">{status?.followerCount ?? 0}</div>
            </div>
          </div>

          {/* Stripe Connect status */}
          <div className="mb-4">
            {status?.hasStripeConnect && status.stripeConnectStatus === 'active' ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-theme-success/10 border border-theme-success/30 rounded-lg">
                <BadgeCheck className="w-4 h-4 text-theme-success" />
                <span className="text-sm text-theme-success font-medium">Stripe Connected</span>
              </div>
            ) : status?.hasStripeConnect && status.stripeConnectStatus === 'restricted' ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-amber-400 font-medium">
                    Action Required — Stripe needs additional verification
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleConnectStripe}
                  disabled={connectLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors text-sm disabled:opacity-50"
                >
                  <LinkIcon className="w-4 h-4" />
                  {connectLoading ? 'Redirecting...' : 'Complete Verification'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleConnectStripe}
                disabled={connectLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                <LinkIcon className="w-4 h-4" />
                {connectLoading ? 'Redirecting...' : 'Connect Stripe to Get Paid'}
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onOpenEarningsDashboard}
            className="inline-flex items-center gap-2 px-4 py-2 bg-theme-surface-hover hover:bg-theme-border text-theme-text font-medium rounded-lg transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            View Full Dashboard
          </button>
        </div>
      )}
    </div>
  );
}
